import moment from 'moment';
import {
  DatePickerInputField,
  InputField,
  RadioField,
} from '../../../../components/FormikFields';
import MultiCheckboxField from '../../../../components/FormikFields/MultiCheckboxField';
import { validationSchema } from '../../filters/filters.config';
import { FilterMatchMode } from 'primereact/api';
import {
  dateRangeFilterTemplate,
  singleSelectFilterTemplate,
  textFilterTemplate,
  timeRangeFilterTemplate,
  timeRangeFilterFunction,
} from '../../../../components/shared/DataTable/filterComponents';

const INVOICE_TYPE_OPTIONS = [
  { label: 'Sales', value: 'Sales' },
  { label: 'Purchase', value: 'Purchase' },
  { label: 'Third Party', value: 'Third Party' },
];

const INVOICE_SUBTYPE_OPTIONS = [
  { label: 'VAT E-Invoice', value: 'VAT E-Invoice' },
  { label: 'Debit note', value: 'Debit note' },
  { label: 'Credit note', value: 'Credit note' },
];

const GENERATED_BY_OPTIONS = [
  { label: 'Portal', value: 'Portal' },
  { label: 'ERP', value: 'ERP' },
  { label: 'API', value: 'API' },
];

export const COLUMNS = ({ t, invoiceType }) => [
  {
    label: 'invoice_list.issue_date',
    value: 'issueDate',
    body: (rowData) =>
      rowData?.issueDate
        ? moment.unix(rowData.issueDate).format('DD-MM-YYYY')
        : '-',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    isDateColumn: true,
    dataType: 'date',
    filterMatchMode: FilterMatchMode.CUSTOM,
    filterElement: dateRangeFilterTemplate,
    filterFunction: (value, filter) => {
      if (!filter || (!filter[0] && !filter[1])) {
        return true;
      }
      if (!value) {
        return false;
      }
      // value is a unix timestamp
      const date = moment.unix(value);
      const startDate = filter[0] ? moment(filter[0]).startOf('day') : null;
      const endDate = filter[1] ? moment(filter[1]).endOf('day') : null;

      if (startDate && endDate) {
        return date.isBetween(startDate, endDate, null, '[]');
      } else if (startDate) {
        return date.isSameOrAfter(startDate);
      } else if (endDate) {
        return date.isSameOrBefore(endDate);
      }
      return true;
    },
  },
  {
    label: 'invoice_list.issue_time',
    value: 'issueTime',
    body: (rowData) => rowData?.issueTime || '-',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    dataType: 'time',
    filterMatchMode: FilterMatchMode.CUSTOM,
    filterElement: timeRangeFilterTemplate,
    filterFunction: timeRangeFilterFunction,
  },
  {
    label: 'invoice_list.type',
    value: 'invoiceType',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.EQUALS,
    filterElement: (options) =>
      singleSelectFilterTemplate(options, INVOICE_TYPE_OPTIONS),
  },
  {
    label: 'invoice_list.transaction_type',
    value: 'invoiceSubtype',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.EQUALS,
    filterElement: (options) =>
      singleSelectFilterTemplate(options, INVOICE_SUBTYPE_OPTIONS),
  },
  {
    label: 'invoice_list.e_invoice_number',
    value: 'invoiceNumber',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterElement: (options) =>
      textFilterTemplate(
        options,
        t('search_by', { column: t('invoice_list.e_invoice_number') }),
      ),
  },
  {
    label: 'invoice_list.generated_by',
    value: 'generatedBy',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.EQUALS,
    filterElement: (options) =>
      singleSelectFilterTemplate(options, GENERATED_BY_OPTIONS),
  },
  {
    label:
      invoiceType === 'SALES'
        ? 'invoice_list.customer_name'
        : invoiceType === 'PURCHASES'
        ? 'invoice_list.supplier_name'
        : 'invoice_list.supplier_customer_name',
    value: 'buyerName',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterElement: (options) =>
      textFilterTemplate(
        options,
        t('search_by', {
          column: t(
            invoiceType === 'SALES'
              ? 'invoice_list.customer_name'
              : invoiceType === 'PURCHASES'
              ? 'invoice_list.supplier_name'
              : 'invoice_list.supplier_customer_name',
          ),
        }),
      ),
  },
  {
    label: 'invoice_list.vat_number',
    value: 'buyerVat',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterElement: (options) =>
      textFilterTemplate(
        options,
        t('search_by', { column: t('invoice_list.vat_number') }),
      ),
  },
  {
    label: 'invoice_list.currency',
    value: 'currencyCode',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterElement: (options) =>
      textFilterTemplate(
        options,
        t('search_by', { column: t('invoice_list.currency') }),
      ),
  },
  {
    label: 'invoice_list.net-amount-excluding-vat',
    value: 'netAmount',
    body: (rowData) => new Intl.NumberFormat().format(rowData.netAmount) || '-',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterElement: (options) =>
      textFilterTemplate(
        options,
        t('search_by', { column: t('invoice_list.net-amount-excluding-vat') }),
      ),
  },
  {
    label: 'invoice_list.vat_amount',
    value: 'vatAmount',
    body: (rowData) => new Intl.NumberFormat().format(rowData.vatAmount) || '-',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterElement: (options) =>
      textFilterTemplate(
        options,
        t('search_by', { column: t('invoice_list.vat_amount') }),
      ),
  },
  {
    label: 'invoice_list.gross-total-including-vat',
    value: 'grossAmount',
    body: (rowData) =>
      new Intl.NumberFormat().format(rowData.grossAmount) || '-',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterElement: (options) =>
      textFilterTemplate(
        options,
        t('search_by', { column: t('invoice_list.gross-total-including-vat') }),
      ),
  },
];

export const FILTERS_CONFIG = {
  validationSchema: validationSchema,
  filters: (values, filters, styles, t) => [
    {
      title: t('invoice_list.issue_date'),
      fields: [...ISSUE_DATE_FIELDS(values, t)],
    },
    {
      title: t('invoice_list.vat_number_label'),
      fields: [
        {
          name: 'vatAccountType',
          component: RadioField,
          controlProps: { className: 'w-100' },
          componentProps: {
            options: [
              { value: 'customer', label: 'invoice_list.customer' },
              { value: 'third Party', label: 'invoice_list.third_party' },
            ],
          },
        },
        {
          name: 'vatAccountNumber',
          component: InputField,
          controlProps: { className: 'w-100' },
          componentProps: {
            className: styles.datepicker,
            placeholder: t('invoice_list.enter_exact_vat_number'),
            disabled: !values?.vatAccountType,
          },
        },
      ],
    },
    {
      title: t('invoice_list.net_amount'),
      fields: [
        {
          name: 'netMinAmount',
          component: InputField,
          controlProps: { className: 'col' },
          componentProps: {
            placeholder: t('invoice_list.from'),
          },
        },
        {
          name: 'netMaxAmount',
          component: InputField,
          controlProps: { className: 'col' },
          componentProps: {
            placeholder: t('invoice_list.to'),
          },
        },
      ],
    },
    {
      title: t('invoice_list.vat_amount'),
      fields: [
        {
          name: 'vatMinAmount',
          component: InputField,
          controlProps: { className: 'col' },
          componentProps: {
            placeholder: t('invoice_list.from'),
          },
        },
        {
          name: 'vatMaxAmount',
          component: InputField,
          controlProps: { className: 'col' },
          componentProps: {
            placeholder: t('invoice_list.to'),
          },
        },
      ],
    },
    {
      title: t('invoice_list.gross_amount'),
      fields: [
        {
          name: 'grossMinAmount',
          component: InputField,
          controlProps: { className: 'col' },
          componentProps: {
            placeholder: t('invoice_list.from'),
          },
        },
        {
          name: 'grossMaxAmount',
          component: InputField,
          controlProps: { className: 'col' },
          componentProps: {
            placeholder: t('invoice_list.to'),
          },
        },
      ],
    },
  ],
  moreFilters: (values, filters, styles, t) => [
    {
      title: t('invoice_list.e_invoice_number'),
      fields: [
        {
          name: 'invoiceNumber',
          component: InputField,
          controlProps: {
            className: 'col',
            tooltip: t('invoice_list.enter_exact_e_invoice_number'),
          },
          componentProps: {
            className: styles.datepicker,
            placeholder: t('invoice_list.enter_exact_e_invoice_number'),
          },
        },
      ],
    },
    {
      title: t('invoice_list.invoice_sub_type'),
      fields: [
        {
          name: 'invoiceSubType',
          component: MultiCheckboxField,
          controlProps: { className: 'w-100' },
          componentProps: {
            className: styles.datepicker,
            value: values?.subType,
            options: [
              { name: 'VAT_INVOICE', label: t('invoice_list.vat') },
              { name: 'DEBIT_NOTE', label: t('invoice_list.debit') },
              { name: 'CREDIT_NOTE', label: t('invoice_list.credit') },
            ],
          },
        },
      ],
    },
    {
      title: t('invoice_list.full_or_simplified'),
      fields: [
        {
          name: 'fullOrSimplified',
          component: MultiCheckboxField,
          componentProps: {
            options: INVOICES_TYPES({ t }),
          },
        },
      ],
    },
    {
      title: t('invoice_list.special_scenario'),
      fields: [
        {
          name: 'transactionType',
          component: MultiCheckboxField,
          componentProps: {
            options: SPECIAL_SCENARIOS({ t }),
          },
        },
      ],
    },
    {
      title: t('invoice_list.generated_by'),
      fields: [
        {
          name: 'generatedBy',
          component: MultiCheckboxField,
          componentProps: {
            options: GENERATED_BY({ t }),
          },
        },
      ],
    },
    {
      title: t('invoice_list.issue_time'),
      fields: [
        {
          name: 'issueTimeFrom',
          component: DatePickerInputField,
          componentProps: {
            placeholder: t('select-start-time'),
            timeOnly: true,
            showTime: true,
            hourFormat: 24,
            minDate: null,
            maxDate: null,
            dateFormat: 'HH:mm:ss',
            value: values?.issueTimeFrom,
          },
        },
        {
          name: 'issueTimeTo',
          component: DatePickerInputField,
          componentProps: {
            timeOnly: true,
            showTime: true,
            hourFormat: 24,
            dateFormat: 'HH:mm:ss',
            placeholder: t('select-end-time'),
            minDate: values?.issueTimeFrom,
            value: values?.issueTimeTo,
          },
        },
      ],
    },
  ],
  moreFiltersLabel: (t) => t('invoice_list.more_filters'),
  filtersValues: [],
};

const SPECIAL_SCENARIOS = ({ t }) => [
  { name: 'isSelfBilling', label: t('invoice_list.self_billing') },
  { name: 'isThirdParty', label: t('invoice_list.third_party') },
  { name: 'isSummaryInvoice', label: t('invoice_list.summary_e_invoice') },
  { name: 'isExportInvoice', label: t('invoice_list.export_e_invoice') },
  { name: 'isRcmInvoice', label: t('invoice_list.rcm_e_invoice') },
  { name: 'isDeemedSupply', label: t('invoice_list.deemed_supply') },
  { name: 'isPrepaymentInvoice', label: t('invoice_list.prepayment_invoice') },
];

const GENERATED_BY = ({ t }) => [
  { name: 'DEVICE', label: t('invoice_list.device') },
  { name: 'WEB', label: t('invoice_list.web_application') },
];

const INVOICES_TYPES = ({ t }) => [
  { name: 'STANDARD_INVOICE', label: t('invoice_list.full') },
  { name: 'SIMPLIFIED_INVOICE', label: t('invoice_list.simplified') },
];

const ISSUE_DATE_FIELDS = (values, t) => {
  if (values?.issueDateType === 'specificDate') {
    return [
      {
        name: 'issueDate',
        component: DatePickerInputField,
        controlProps: { className: 'w-100' },
        componentProps: {
          placeholder: t('select_date'),
          value: values?.issueDateFrom,
          maxDate: new Date(),
        },
      },
    ];
  } else if (values?.issueDateType === 'dateRange') {
    return [
      {
        name: 'issueDateFrom',
        component: DatePickerInputField,
        componentProps: {
          placeholder: t('invoice_list.select_start_date'),
          value: values?.issueDateFrom,
          maxDate: values?.issueDateTo ? values?.issueDateTo : new Date(),
        },
      },
      {
        name: 'issueDateTo',
        component: DatePickerInputField,
        componentProps: {
          placeholder: t('select_end_date'),
          maxDate: new Date(),
          minDate: values?.issueDateFrom,
          value: values?.issueDateTo,
        },
      },
    ];
  }
  return [];
};

// PrimeReact popup column filters need initial filter models for each field.
// Note: CUSTOM filters should use simple { value, matchMode } structure, not constraints
// Constraints are only for built-in filter modes (CONTAINS, STARTS_WITH, etc.)
export const SEARCH_CONFIG = {
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  issueDate: { value: null, matchMode: FilterMatchMode.CUSTOM },
  issueTime: { value: null, matchMode: FilterMatchMode.CUSTOM },
  invoiceType: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  invoiceSubtype: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  invoiceNumber: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  generatedBy: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  buyerName: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  buyerVat: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  currencyCode: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  netAmount: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  vatAmount: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  grossAmount: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
};
